import * as React from "react";

import { cn } from "@/lib/utils";

export interface AutosizeTextareaProps extends React.ComponentProps<"textarea"> {
  /** Hard cap before the field starts scrolling (px). Default 360. */
  maxHeight?: number;
  /** Minimum height (px). Default 40. */
  minHeight?: number;
  /**
   * When set, the field clamps to this height and shows a "Ver mais" toggle
   * once the content is taller. Expanding raises the cap to `maxHeight`.
   */
  collapsedMaxHeight?: number;
  /** Label for the expand action when collapsed. Default "Ver mais". */
  expandLabel?: string;
  /** Label for the collapse action when expanded. Default "Ver menos". */
  collapseLabel?: string;
}

/**
 * Textarea that grows to fit its content (up to `maxHeight`, then scrolls).
 * Recomputes height whenever `value` changes, so AI-prefilled text shows in
 * full instead of being clipped into a fixed-height box.
 *
 * Pass `collapsedMaxHeight` to keep long content from dominating the form: the
 * field clamps to that height and a text-only "Ver mais"/"Ver menos" toggle
 * appears (no icons, per Comp UI conventions).
 */
const AutosizeTextarea = React.forwardRef<HTMLTextAreaElement, AutosizeTextareaProps>(
  (
    {
      className,
      value,
      maxHeight = 360,
      minHeight = 40,
      collapsedMaxHeight,
      expandLabel = "Ver mais",
      collapseLabel = "Ver menos",
      onChange,
      ...props
    },
    forwardedRef,
  ) => {
    const innerRef = React.useRef<HTMLTextAreaElement | null>(null);
    const [expanded, setExpanded] = React.useState(false);
    const [overflowing, setOverflowing] = React.useState(false);

    const setRefs = React.useCallback(
      (node: HTMLTextAreaElement | null) => {
        innerRef.current = node;
        if (typeof forwardedRef === "function") {
          forwardedRef(node);
        } else if (forwardedRef) {
          (forwardedRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
        }
      },
      [forwardedRef],
    );

    const resize = React.useCallback(() => {
      const el = innerRef.current;
      if (!el) return;
      el.style.height = "auto";
      const full = el.scrollHeight;
      const collapseCap = collapsedMaxHeight ?? maxHeight;
      setOverflowing(collapsedMaxHeight != null && full > collapseCap + 1);
      const cap = expanded ? maxHeight : collapseCap;
      const next = Math.max(minHeight, Math.min(full, cap));
      el.style.height = `${next}px`;
      el.style.overflowY = full > cap + 1 ? "auto" : "hidden";
    }, [collapsedMaxHeight, maxHeight, minHeight, expanded]);

    // Recompute when the value changes externally (e.g. AI draft applied).
    // `value` is intentionally a dependency even though `resize` reads the DOM,
    // not the prop — we want the height recalculated on every value change.
    // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure on value change
    React.useLayoutEffect(() => {
      resize();
    }, [resize, value]);

    React.useEffect(() => {
      const onWindowResize = () => resize();
      window.addEventListener("resize", onWindowResize);
      return () => window.removeEventListener("resize", onWindowResize);
    }, [resize]);

    return (
      <div className="w-full">
        <textarea
          ref={setRefs}
          value={value}
          onChange={(e) => {
            onChange?.(e);
            resize();
          }}
          className={cn(
            "flex w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
            className,
          )}
          {...props}
        />
        {collapsedMaxHeight != null && overflowing && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-1 text-xs font-medium text-muted-foreground hover:text-foreground"
          >
            {expanded ? collapseLabel : expandLabel}
          </button>
        )}
      </div>
    );
  },
);
AutosizeTextarea.displayName = "AutosizeTextarea";

export { AutosizeTextarea };
